/**
 * @file ManageTaskCategoriesModal.tsx
 * @brief Modal to view and manage task categories from enum_master.
 * @author Nestbotics
 * @date 2026-06-19
 */

import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  TextField,
  Button,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Divider,
} from '@mui/material';
import { Plus, Trash2, Tag, X } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  enumMasterService,
  TASK_CATEGORY_KEY,
  TASK_CATEGORY_MODULE,
  type EnumMasterItem,
} from '@/services/api';
import { QUERY_KEYS } from '@/services/queryClient';

export interface ManageTaskCategoriesModalProps {
  open: boolean;
  onClose: () => void;
  /** Called when a new category is created so the parent form can select it. */
  onCategoryAdded?: (keyValue: string) => void;
}

/**
 * ManageTaskCategoriesModal displays current task categories and lets managers add or deactivate them.
 */
const ManageTaskCategoriesModal: React.FC<ManageTaskCategoriesModalProps> = ({
  open,
  onClose,
  onCategoryAdded,
}) => {
  const queryClient = useQueryClient();
  const [newLabel, setNewLabel] = useState('');
  const [confirmItem, setConfirmItem] = useState<EnumMasterItem | null>(null);

  const { data: categories = [], isLoading, error } = useQuery({
    queryKey: QUERY_KEYS.taskCategories(true),
    queryFn: () => enumMasterService.listTaskCategories(true),
    enabled: open,
    staleTime: 30_000,
  });

  const createMutation = useMutation({
    mutationFn: (displayLabel: string) =>
      enumMasterService.create({
        moduleName: TASK_CATEGORY_MODULE,
        keyName: TASK_CATEGORY_KEY,
        displayLabel,
      }),
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: ['enum-master', TASK_CATEGORY_MODULE, TASK_CATEGORY_KEY] });
      setNewLabel('');
      onCategoryAdded?.(created.keyValue);
    },
  });

  const deactivateMutation = useMutation({
    mutationFn: (enumId: string) => enumMasterService.deactivate(enumId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['enum-master', TASK_CATEGORY_MODULE, TASK_CATEGORY_KEY] });
      setConfirmItem(null);
    },
  });

  const reactivateMutation = useMutation({
    mutationFn: (enumId: string) => enumMasterService.update(enumId, { isActive: true }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['enum-master', TASK_CATEGORY_MODULE, TASK_CATEGORY_KEY] });
    },
  });

  const activeCategories = categories.filter((item) => item.isActive);
  const inactiveCategories = categories.filter((item) => !item.isActive);

  const handleAdd = () => {
    const label = newLabel.trim();
    if (!label) return;
    createMutation.mutate(label);
  };

  const handleClose = () => {
    setNewLabel('');
    setConfirmItem(null);
    onClose();
  };

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Tag size={20} />
            Manage Task Categories
          </Box>
          <IconButton size="small" onClick={handleClose} aria-label="Close">
            <X size={18} />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Categories are shared across all tasks. Remove a category from the dropdown with the trash icon,
            or restore inactive ones below.
          </Typography>

          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <TextField
              size="small"
              fullWidth
              label="New category name"
              placeholder="e.g. QA Review"
              value={newLabel}
              onChange={(e) => setNewLabel(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleAdd();
                }
              }}
            />
            <Button
              variant="contained"
              startIcon={<Plus size={16} />}
              onClick={handleAdd}
              disabled={!newLabel.trim() || createMutation.isPending}
              sx={{ whiteSpace: 'nowrap' }}
            >
              Add
            </Button>
          </Box>

          {createMutation.isError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {(createMutation.error as Error).message || 'Failed to add category'}
            </Alert>
          )}

          <Divider sx={{ my: 2 }} />

          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={28} />
            </Box>
          ) : error ? (
            <Alert severity="error">Failed to load categories.</Alert>
          ) : (
            <>
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                Current Categories ({activeCategories.length})
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: inactiveCategories.length > 0 ? 2 : 0 }}>
                {activeCategories.map((item) => (
                  <Chip
                    key={item.id}
                    label={item.displayLabel}
                    onDelete={() => setConfirmItem(item)}
                    deleteIcon={<Trash2 size={14} />}
                  />
                ))}
              </Box>

              {inactiveCategories.length > 0 && (
                <>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    Inactive ({inactiveCategories.length})
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {inactiveCategories.map((item) => (
                      <Chip
                        key={item.id}
                        label={item.displayLabel}
                        variant="outlined"
                        size="small"
                        onClick={() => reactivateMutation.mutate(item.id)}
                        sx={{ cursor: 'pointer' }}
                      />
                    ))}
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                    Click an inactive category to restore it.
                  </Typography>
                </>
              )}
            </>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose}>Done</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(confirmItem)} onClose={() => setConfirmItem(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Deactivate Category</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Deactivate &quot;{confirmItem?.displayLabel}&quot;? Existing tasks keep their value,
            but it will no longer appear in the dropdown.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmItem(null)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => confirmItem && deactivateMutation.mutate(confirmItem.id)}
            disabled={deactivateMutation.isPending}
          >
            Deactivate
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ManageTaskCategoriesModal;
